/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractService, CommonService, Config } from '@increff/commons';
import { Observable } from 'rxjs';
import { TEMPLATE_GUIDE, USER } from '../../interfaces/user.interface';
import { Rest } from '../rest.service';

@Injectable({
    providedIn: 'root'
})
export class UsersService {

    constructor(
      private abstractService: AbstractService,
      private urls: Rest,
      private commonService: CommonService,
      private config: Config
    ) { }

    // Template Guides
    getBulkAddUsersTemplateGuideData(): Observable<TEMPLATE_GUIDE[]> {
        return this.abstractService.exec(this.urls.bulkAddUsersTemplateGuideDataUrl, 'GET');
    }

    getUserMigrationTemplateGuideData(): Observable<TEMPLATE_GUIDE[]>{
        return this.abstractService.exec(this.urls.userMigrationTemplateGuideDataUrl, 'GET');
    }

    getBulkUpdateUsersTemplateGuideData(): Observable<TEMPLATE_GUIDE[]> {
        return this.abstractService.exec(this.urls.bulkUpdateUsersTemplateGuideDataUrl, 'GET');
    }

    getAppDataFromStorage(): {appName: string; domainId: number} {
        const chosenAppDataStr = localStorage?.getItem(this.commonService?.cipher('lastChosenAppData'));
        if(chosenAppDataStr) return JSON.parse(this.commonService?.decipher(chosenAppDataStr));

        this.setAppDataInStorage(this.config?.startupData?.defaultAppName, this.config?.startupData?.defaultDomainId);
        return {appName:this.config?.startupData?.defaultAppName, domainId: this.config?.startupData?.defaultDomainId };
    }

    setAppDataInStorage(appName: string, domainId: number): void {
        localStorage?.setItem(this.commonService?.cipher('lastChosenAppData'), this.commonService?.cipher(JSON.stringify({appName, domainId})));
    }

    getAllUsers(domainId: number, type: 'admin' | 'domain' = 'admin'): Observable<USER[]> {
        const params = new HttpParams()?.set('domainId', domainId);
        return this.abstractService.exec(
            type === 'admin' ? this.urls.adminUsersUrl : this.urls.allDomainUsersUrl,
            'GET',
            undefined,
            type === 'admin' ? {params, ...this.config?.startupData?.extraHttpOptions} : {...this.config?.startupData?.extraHttpOptions}
        );
    }

    getUserDetailsById(userId: number, domainId?: number, type: 'admin' | 'domain' = 'admin'): Observable<USER> {
        // const params = new HttpParams()?.set('domainId', domainId);
        return this.abstractService.exec(
            type === 'admin' ? this.urls?.getAdminUserDetailsByIdUrl(userId, domainId) : this.urls?.getDomainUserDetailsByIdUrl(userId),
            'GET',
            undefined,
            {...this.config?.startupData?.extraHttpOptions}
        );
    }

    getUsersForSpecificResourceType(resourceId: number, domainResourceId?: number): Observable<USER[]> {
        const headers = new HttpHeaders()?.set('domainResourceId', `${domainResourceId}`);
        return this.abstractService.exec(
            this.urls?.getAllDomainUserForResourceTypeUrl(resourceId),
            'GET',
            undefined,
            domainResourceId ? { headers , ...this.config?.startupData?.extraHttpOptions} : {...this.config?.startupData?.extraHttpOptions}
        );
    }

    // Super Admin User API Methods

    updateUserDetails(userId: number, domainId: number, userDetails: USER, type: 'admin' | 'domain' = 'admin'): Observable<USER> {
        let params = new HttpParams()?.set('id', userId);
        type === 'admin' ? params = params?.set('domainId', domainId) : '';
        return this.abstractService?.exec(
            type === 'admin' ? this.urls.getUpdateAdminUserDetailsUrl(userId) : this.urls.getUpdateDomainUserDetailsUrl(userId),
            'PUT',
            userDetails,
            type === 'admin' ? {params, ...this.config?.startupData?.extraHttpOptions} : {...this.config?.startupData?.extraHttpOptions}
        );
    }

    changeUserState(userId: number, domainId: number, enabled: boolean, type: 'admin' | 'domain' = 'admin'): Observable<void> {
        let params = new HttpParams()?.set('enabled', enabled);
        type === 'admin' ? params = params?.set('domainId', domainId) : '';
        return this.abstractService?.exec(
            type === 'admin' ? this.urls.getToggleAdminUserStateUrl(userId) : this.urls.getToggleDomainUserStateUrl(userId),
            'PUT',
            undefined,
            {params, ...this.config?.startupData?.extraHttpOptions}
        );
    }

    changeUserVerificationStatus(userId: number, domainId: number, enabled: boolean, type: 'admin' | 'domain' = 'admin'): Observable<void>{
        let params = new HttpParams()?.set('enabled', enabled);
        type === 'admin' ? params = params?.set('domainId', domainId) : '';
        return this.abstractService?.exec(
            type === 'admin' ? this.urls.getToggleAdminUserVerificationStatusUrl(userId) : this.urls.getToggleDomainUserVerificationStatusUrl(userId),
            'PUT',
            undefined,
            {params, ...this.config?.startupData?.extraHttpOptions}
        );
    }

    changeUserMFAStatus(userId: number, domainId: number, enabled: boolean, type: 'admin' | 'domain' = 'admin'): Observable<void> {
        let params = new HttpParams()?.set('enabled', enabled);
        type === 'admin' ? params = params?.set('domainId', domainId): '';
        return this.abstractService?.exec(
            type === 'admin' ? this.urls.getToggleAdminUserMFAStatusUrl(userId) : this.urls.getToggleDomainUserMFAStatusUrl(userId),
            'PUT',
            undefined,
            {params, ...this.config?.startupData?.extraHttpOptions}
        );
    }

    addNewUser(appName: string, domainId: number, userDetails: USER, type: 'admin' | 'domain' = 'admin'): Observable<USER> {
        const params = new HttpParams()?.set('appName', appName)?.set('domainId', domainId);
        return this.abstractService?.exec(
            type === 'admin' ? this.urls.addAdminUserUrl : this.urls.addDomainUserUrl,
            'POST',
            userDetails,
            type === 'admin' ? {params, ...this.config?.startupData?.extraHttpOptions} : {...this.config?.startupData?.extraHttpOptions}
        );
    }

    changeUserPassword(userId: number, password: string, type: 'admin' | 'domain' = 'admin', domainId?: number): Observable<void> {
        const params = new HttpParams()?.set('password', password)?.set('domainId', domainId);
        return this.abstractService?.exec(
            type === 'admin' ? this.urls?.getAdminChangeUserPasswordUrl(userId) : this.urls?.getDomainUserChangePasswordUrl(userId),
            'PUT',
            undefined,
            {params, ...this.config?.startupData?.extraHttpOptions}
        );
    }

    changeUserLoginMode(userId: number, domainId: number, details: any, type: 'admin' | 'domain' = 'admin'): Observable<void>{
        const params = new HttpParams()?.set('domainId', domainId);
        return this.abstractService?.exec(
            type === 'admin' ? this.urls.getAdminChangeUserLoginModeUrl(userId) : this.urls.getDomainUserChangeLoginModeUrl(userId),
            'PUT',
            details,
            type === 'admin' ? {params, ...this.config?.startupData?.extraHttpOptions} : {...this.config?.startupData?.extraHttpOptions}
        );
    }

    resetUser(userId: number, domainId: number, type: 'admin' | 'domain' = 'admin'): Observable<void> {
        const params = new HttpParams()?.set('domainId', domainId);
        return this.abstractService?.exec(
            type === 'admin' ? this.urls.getResetAdminUserUrl(userId) : this.urls.getResetDomainUserUrl(userId),
            'PUT',
            undefined,
            type === 'admin' ? {params, ...this.config?.startupData?.extraHttpOptions} : {...this.config?.startupData?.extraHttpOptions}
        );
    }

    deleteUsersByEmail(mailIds: string[] = []): Observable<void> {
        return this.abstractService?.exec(
            this.urls?.adminDeleteUsersByEmailUrl,
            'PUT',
            mailIds
        );
    }

    bulkAddUserRoles(formData: FormData, domainId: number, type: 'admin' | 'domain' = 'admin'): Observable<Blob> {
        const params = new HttpParams()?.set('domainId', domainId);
        return this.abstractService.exec(
            type === 'admin' ? this.urls?.adminBulkUploadUserRolesUrl : this.urls?.domainBulkUploadUserRolesUrl,
            'POST',
            formData,
            { responseType: 'application/octet-stream', params }
        );
    }

    bulkAddUsers(formData: FormData, domainId: number, type: 'admin' | 'domain' = 'admin'): Observable<Blob> {
        const params = new HttpParams()?.set('domainId', domainId);
        return this.abstractService.exec(
            type === 'admin' ? this.urls?.adminBulkAddUsersUrl : this.urls?.domainBulkAddUsersUrl,
            'POST',
            formData,
            { responseType: 'application/octet-stream', params }
        );
    }

    getUserDetailsReport(domainId: number, type: 'admin' | 'domain' = 'admin'): Observable<Blob> {
        let params = new HttpParams();
        if(type === 'admin') params = params?.set('domainId', domainId);
        return this.abstractService.exec(
            type === 'admin' ? this.urls.adminUserDetailsReportUrl : this.urls.domainUserDetailsReportUrl,
            'GET',
            undefined,
            {responseType: 'application/octet-stream', params}
        );
    }

    // Domain User API Controller
    getDomainUserDetails(): Observable<USER> {
        return this.abstractService?.exec(this.urls.domainUserDetailsUrl, 'GET', undefined, {...this.config?.startupData?.extraHttpOptions});
    }

    updateDomainUserDetails(userDetails: USER): Observable<USER> {
        return this.abstractService?.exec(this.urls.updateDomainUserDetailsUrl, 'PUT', userDetails, {...this.config?.startupData?.extraHttpOptions});
    }

    changeDomainUserPassword(passwordDetails: {oldPassword: string; newPassword: string}): Observable<void> {
        return this.abstractService?.exec(this.urls.changeDomainUserPasswordUrl, 'PUT', passwordDetails, {...this.config?.startupData?.extraHttpOptions});
    }

    sendVerificationEmail(): Observable<void> {
        return this.abstractService?.exec(this.urls.sendVerificationEmailUrl, 'GET');
    }
}
